/**
 * Wave 3.99z / 3.99aa — Mountings per-item ledger route page.
 *
 * Wave 3.99aa columns per operator spec:
 *   Date | Particulars | Received (Grams · Rate) | Issued (Grams) | Balance (Grams · Value)
 * where:
 *   • "Received (Grams · Rate)" — grams added on a purchase + the
 *     per-gram rate paid (audit only).
 *   • "Issued (Grams)"           — grams reduced (issue / void adjustment).
 *   • "Balance (Grams · Value)"  — running grams + LIVE gold-marked
 *     value = balance_grams × mounting_purity_% × today's 999 gold rate.
 *
 * Value ALWAYS marks to the live daily gold rate; purchase-cost is
 * captured only for the ledger's audit column and never influences
 * the balance.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '@/components/AppShell';
import CompanyGate from '@/components/CompanyGate';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  ArrowLeft, Coins, ScrollText, PackagePlus, ArrowUpFromLine,
  RotateCcw, AlertTriangle,
} from 'lucide-react';
import { formatDateTime } from '@/lib/format';
import { extractError } from '@/lib/errors';

// Compact INR formatter — never a floating $, no fractional paise.
const formatINR = (n) => {
  const v = Number(n || 0);
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency', currency: 'INR', maximumFractionDigits: 0,
    }).format(v);
  } catch {
    return `₹${Math.round(v).toLocaleString('en-IN')}`;
  }
};

export default function MountingLedger() {
  return (
    <AppShell
      title="Mounting Ledger"
      subtitle="Per-item movement history. Purchases add stock; issues to orders reduce it. Balance value marks to live gold rate."
    >
      <CompanyGate>
        <MountingLedgerContent />
      </CompanyGate>
    </AppShell>
  );
}

function MountingLedgerContent() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [mounting, setMounting] = useState(null);
  const [rows, setRows] = useState([]);
  const [closing, setClosing] = useState(0);
  const [closingValue, setClosingValue] = useState(0);
  const [goldRate, setGoldRate] = useState(0);
  const [purityPct, setPurityPct] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const { data } = await api.get(`/mountings/${id}/ledger`);
        if (cancelled) return;
        setMounting(data.mounting || null);
        setRows(Array.isArray(data.rows) ? data.rows : []);
        setClosing(Number(data.closing_stock_grams || 0));
        setClosingValue(Number(data.closing_stock_value || 0));
        setGoldRate(Number(data.gold_rate_per_gram_999 || 0));
        setPurityPct(Number(data.purity_percent || 0));
      } catch (e) {
        if (!cancelled) toast.error(extractError(e, 'Failed to load ledger'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [id]);

  const totals = useMemo(() => {
    let purchases = 0;
    let issued = 0;
    let voided = 0;
    let mismatches = 0;
    for (const r of rows) {
      const g = Number(r.weight_grams_delta || 0);
      if (r.entry_type === 'purchase') purchases += g;
      else if (r.entry_type === 'issue') issued += Math.abs(g);
      else if (r.entry_type === 'void') voided += g;
      if (r.purity_mismatch) mismatches += 1;
    }
    return {
      purchases: +purchases.toFixed(3),
      issued: +issued.toFixed(3),
      voided: +voided.toFixed(3),
      mismatches,
    };
  }, [rows]);

  const particulars = (r) => {
    const type = r.entry_type || 'purchase';
    if (type === 'purchase') {
      const ref = r.reference_id ? ` — ${r.reference_id}` : '';
      return `Purchase${ref}`;
    }
    if (type === 'issue') {
      if (r.order_no) return `Issued to Order ${r.order_no}`;
      return 'Issued';
    }
    if (type === 'void') return 'Void / returned to stock';
    if (type === 'return') return 'Returned';
    if (type === 'adjustment') return 'Adjustment';
    return type;
  };

  return (
    <div className="space-y-4" data-testid="mounting-ledger-page">
      {/* Breadcrumb + back */}
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/mountings')}
          data-testid="mounting-ledger-back"
          className="h-8 px-2"
        >
          <ArrowLeft size={14} className="mr-1" /> Back to Mountings
        </Button>
        <span className="text-[11px] text-slate-400">/</span>
        <span className="text-[11px] text-slate-500 truncate">
          Ledger — {loading ? '…' : (mounting?.name || 'Mounting')}
        </span>
      </div>

      {/* Mounting summary + totals */}
      {loading ? (
        <Card className="p-4">
          <Skeleton className="h-6 w-1/3 mb-2" />
          <Skeleton className="h-4 w-1/2" />
        </Card>
      ) : mounting ? (
        <Card className="p-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Coins size={14} className="text-[var(--gold-accent, #B08D57)]" />
                <div className="text-lg font-semibold text-[var(--ink-navy)]" data-testid="mounting-ledger-name">
                  {mounting.name}
                </div>
                <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-[10.5px]">
                  {mounting.purity_name || '—'}
                </Badge>
                {purityPct > 0 && (
                  <span className="text-[10.5px] text-slate-500 font-mono">
                    ({purityPct.toFixed(2)}%)
                  </span>
                )}
                {mounting.active === false && (
                  <Badge className="bg-slate-100 text-slate-600 text-[10px]">Inactive</Badge>
                )}
              </div>
              {mounting.description && (
                <div className="text-[12px] text-slate-500 max-w-[70ch]" title={mounting.description}>
                  {mounting.description}
                </div>
              )}
              <div className="mt-1 text-[11px] text-slate-500">
                Live gold rate:{' '}
                <span className="font-medium text-slate-700">
                  {goldRate > 0 ? `${formatINR(goldRate)} / g (999)` : 'not set'}
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-widest text-slate-500">Closing Balance</div>
              <div
                className="mt-0.5 text-2xl font-mono tabular-nums text-[var(--ink-navy)]"
                data-testid="mounting-ledger-closing"
              >
                {Number(closing).toFixed(3)} g
              </div>
              <div
                className="mt-0.5 text-[13px] font-mono tabular-nums text-[var(--gold-accent, #B08D57)]"
                data-testid="mounting-ledger-closing-value"
                title="Live gold-rate valuation: grams × purity% × today's 999 rate"
              >
                {formatINR(closingValue)}
              </div>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 grid-auto-wide">
            <MiniStat
              label="Purchases"
              value={`+${totals.purchases.toFixed(3)} g`}
              tone="emerald"
              testId="mounting-ledger-total-purchases"
            />
            <MiniStat
              label="Issued (net)"
              value={`-${totals.issued.toFixed(3)} g`}
              tone="blue"
              testId="mounting-ledger-total-issued"
            />
            <MiniStat
              label="Voided back"
              value={`${totals.voided > 0 ? '+' : ''}${totals.voided.toFixed(3)} g`}
              tone="slate"
              testId="mounting-ledger-total-voided"
            />
            <MiniStat
              label="Purity mismatches"
              value={String(totals.mismatches)}
              tone={totals.mismatches ? 'amber' : 'slate'}
              testId="mounting-ledger-total-mismatches"
            />
          </div>
        </Card>
      ) : (
        <Card className="p-6 text-center text-slate-500" data-testid="mounting-ledger-not-found">
          Mounting not found or you don&apos;t have permission to view it.
        </Card>
      )}

      {/* Ledger table — Wave 3.99aa columns */}
      <Card className="p-0 overflow-hidden">
        <div className="p-3 border-b border-slate-100 flex items-center gap-2">
          <ScrollText size={14} className="text-slate-500" />
          <div className="text-sm font-medium text-[var(--ink-navy)]">Movement History</div>
          <div className="text-[11px] text-slate-500">
            ({rows.length} {rows.length === 1 ? 'entry' : 'entries'})
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="mounting-ledger-table">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr className="text-left">
                <th className="px-3 py-2 font-medium">Date</th>
                <th className="px-3 py-2 font-medium">Particulars</th>
                <th className="px-3 py-2 font-medium text-right">Received (Grams · Rate)</th>
                <th className="px-3 py-2 font-medium text-right">Issued (Grams)</th>
                <th className="px-3 py-2 font-medium text-right">Balance (Grams · Value)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={5} className="text-center py-8 text-slate-500">Loading ledger…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center py-12 text-slate-500" data-testid="mounting-ledger-empty">
                    No ledger entries yet. Record a purchase to add stock, or issue to an order to draw from it.
                  </td>
                </tr>
              )}
              {rows.map((r) => {
                const received = Number(r.received_grams || (Number(r.weight_grams_delta) > 0 ? r.weight_grams_delta : 0) || 0);
                const issued = Number(r.issued_grams || (Number(r.weight_grams_delta) < 0 ? -Number(r.weight_grams_delta) : 0) || 0);
                const rate = r.received_rate_per_gram;
                const bal = Number(r.running_balance_grams || 0);
                const val = Number(r.running_balance_value || 0);
                return (
                  <tr
                    key={r.id}
                    className={`hover:bg-slate-50/60 ${r.entry_type === 'issue' ? 'bg-blue-50/30' : r.entry_type === 'void' ? 'bg-slate-50/50' : ''}`}
                    data-testid={`mounting-ledger-row-${r.id}`}
                  >
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap text-[12.5px]">
                      {formatDateTime(r.timestamp)}
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <EntryChip type={r.entry_type} />
                        <span className="text-[12.5px]">{particulars(r)}</span>
                        {r.order_no && (
                          <a
                            href={`/orders/${r.order_id}`}
                            className="font-mono text-[11.5px] text-blue-700 hover:underline"
                            data-testid={`mounting-ledger-row-order-${r.id}`}
                          >
                            {r.order_no}
                          </a>
                        )}
                        {r.purity_mismatch && (
                          <Badge
                            className="bg-orange-50 text-orange-700 border-orange-200 text-[10px] inline-flex items-center gap-1"
                            title="Purity did not match the order form when this row was recorded"
                          >
                            <AlertTriangle size={9} /> purity mismatch
                          </Badge>
                        )}
                      </div>
                      {r.notes && (
                        <div
                          className="text-[10.5px] text-slate-500 truncate max-w-[280px] mt-0.5"
                          title={r.notes}
                        >
                          {r.notes}
                        </div>
                      )}
                      {r.created_by_email && (
                        <div className="text-[10px] text-slate-400 mt-0.5">
                          by {r.created_by_email}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">
                      {received > 0 ? (
                        <div>
                          <div className="text-emerald-700">+{received.toFixed(3)} g</div>
                          <div className="text-[11px] text-slate-500">
                            @ {rate ? `${formatINR(rate)} / g` : <span className="italic text-slate-400">rate not captured</span>}
                          </div>
                        </div>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">
                      {issued > 0 ? (
                        <span className="text-rose-700">-{issued.toFixed(3)} g</span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">
                      <div className="text-[var(--ink-navy)]">{bal.toFixed(3)} g</div>
                      <div className="text-[11px] text-[var(--gold-accent, #B08D57)]">
                        {formatINR(val)}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function EntryChip({ type }) {
  const styleMap = {
    purchase:   'bg-emerald-50 text-emerald-700 border-emerald-200',
    issue:      'bg-blue-50 text-blue-700 border-blue-200',
    void:       'bg-slate-100 text-slate-600 border-slate-200',
    adjustment: 'bg-amber-50 text-amber-700 border-amber-200',
    return:     'bg-violet-50 text-violet-700 border-violet-200',
  };
  const iconFor = (t) => {
    if (t === 'purchase') return <PackagePlus size={11} />;
    if (t === 'issue') return <ArrowUpFromLine size={11} />;
    if (t === 'void') return <RotateCcw size={11} />;
    return <ScrollText size={11} />;
  };
  return (
    <Badge className={`text-[10px] inline-flex items-center gap-1 ${styleMap[type] || styleMap.purchase}`}>
      {iconFor(type)}
      {type}
    </Badge>
  );
}

function MiniStat({ label, value, tone = 'slate', testId }) {
  const toneClasses = {
    emerald: 'text-emerald-700',
    blue: 'text-blue-700',
    slate: 'text-slate-700',
    amber: 'text-amber-700',
  }[tone] || 'text-slate-700';
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2" data-testid={testId}>
      <div className="text-[10.5px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-sm font-mono tabular-nums font-semibold ${toneClasses}`}>{value}</div>
    </div>
  );
}
